<script>
import formBasicMixin from '@phoenix/mixins/formBasicMixin';
import PersonalDataStep from '@maison/components/form/Gep/PersonalDataStep.vue';
import PostalDataStep from '@maison/components/form/Gep/PostalDataStep.vue';
import WatchDataStep from '@maison/components/form/Gep/WatchDataStep.vue';
import FinalStep from '@maison/components/form/Gep/FinalStep.vue';
import MultiStepForm from '@maison/components/form/MultiStepForm.vue';
import moment from '@phoenix/moment';
import { set } from 'lodash-es';

export default {
  components: {
    MultiStepForm,
  },

  mixins: [formBasicMixin],

  props: {
    languageOptions: {
      type: Array,
      required: true,
    },
    interestsOptions: {
      type: Array,
      required: true,
    },
    groups: {
      type: Object,
      required: true,
    },
    countries: {
      type: Array,
      required: true,
    },
    collections: {
      type: Array,
      required: true,
    },
    stores: {
      type: Array,
      required: true,
    },
    salesforceDistrictOptions: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      fields: {
        country: {
          id: 'country',
          name: 'country',
          label: this.$t('Form.label.country'),
          value: null,
          required: true,
          options: [],
        },
        title: {
          id: 'title',
          name: 'title',
          label: this.$t('Form.label.title'),
          value: '',
          required: true,
        },
        gender: {
          id: 'gender',
          name: 'gender',
          label: this.$t('Form.label.gender'),
          value: '',
          required: true,
        },
        firstName1: {
          id: 'firstName1',
          name: 'firstName1',
          label: this.$t('Form.label.firstName'),
          value: '',
          required: true,
        },
        lastName1: {
          id: 'lastName1',
          name: 'lastName1',
          label: this.$t('Form.label.lastName'),
          value: '',
          required: true,
        },
        firstName2: {
          id: 'firstName2',
          name: 'firstName2',
          label: this.$t('Form.label.firstName2'),
          value: '',
          required: true,
        },
        lastName2: {
          id: 'lastName2',
          name: 'lastName2',
          label: this.$t('Form.label.lastName2'),
          value: '',
          required: true,
        },
        phone: {
          id: 'phone',
          name: 'phone',
          label: this.$t('Form.label.phone'),
          value: '',
          required: true,
        },
        email: {
          id: 'email',
          name: 'email',
          label: this.$t('Form.label.email'),
          value: '',
          required: true,
          type: 'email',
        },
        language: {
          id: 'language',
          name: 'language',
          label: this.$t('Form.label.language'),
          value: null,
          required: true,
          options: [],
        },
        interests: {
          id: 'interests',
          name: 'interests',
          label: this.$t('Form.label.interests'),
          value: null,
          required: true,
          options: [],
        },
        address9: {
          id: 'state',
          name: 'address9',
          label: this.$t('Form.label.state'),
          value: null,
          required: true,
          options: [],
        },
        salesforceDistrict: {
          id: 'salesforceDistrict',
          name: 'salesforceDistrict',
          label: this.$t('Form.label.district'),
          value: null,
          required: true,
          options: [],
        },
        zip: {
          id: 'zip',
          name: 'zip',
          label: this.$t('Form.label.zip'),
          value: '',
          required: true,
        },
        address7: {
          id: 'city',
          name: 'address7',
          label: this.$t('Form.label.city'),
          value: '',
          required: true,
        },
        collection: {
          id: 'collection',
          name: 'collection',
          label: this.$t('Form.label.collection'),
          value: null,
          required: true,
          options: [],
        },
        reference: {
          id: 'reference',
          name: 'reference',
          label: this.$t('Form.label.reference'),
          value: '',
          required: true,
          help: this.$t('Form.help.reference'),
        },
        acquisitionDate: {
          id: 'acquisitionDate',
          name: 'acquisitionDate',
          label: this.$t('Form.label.acquisitionDate'),
          value: '',
          required: true,
          minDate: '1950-01-01',
          maxDate: moment().format('YYYY-MM-DD'),
        },
        store: {
          id: 'store',
          name: 'store',
          label: this.$t('Form.label.store'),
          value: null,
          required: true,
          options: [],
        },
        individualNumber: {
          id: 'individualNumber',
          name: 'individualNumber',
          label: this.$t('Form.label.individualNumber'),
          value: '',
          required: true,
        },
        newsletter: {
          id: 'newsletter',
          name: 'newsletter',
          label: this.$t('Form.newsletter'),
          value: false,
        },
      },
    };
  },

  computed: {
    steps() {
      const steps = [
        {
          step: 1,
          component: PersonalDataStep,
          attrs: {
            stepFields: [
              'country',
              'title',
              'gender',
              'firstName1',
              'lastName1',
              'firstName2',
              'lastName2',
              'phone',
              'email',
              'language',
              'interests',
            ],
          },
        },
        {
          step: 2,
          component: PostalDataStep,
          attrs: {
            stepFields: ['address9', 'salesforceDistrict', 'zip', 'address7'],
          },
          enabled: {
            country: ['CA', 'US'],
          },
        },
        {
          step: 3,
          component: WatchDataStep,
          attrs: {
            stepFields: ['collection', 'reference', 'acquisitionDate', 'store', 'individualNumber'],
          },
        },
      ];

      return [
        ...steps,
        {
          step: 4,
          component: FinalStep,
          attrs: {
            stepFields: ['newsletter'],
            privacyLink: this.terms,
            steps,
          },
        },
      ];
    },

    formForMultiStep() {
      return { ...this.form, fields: this.fields };
    },
  },

  watch: {
    languageOptions: {
      handler(value) {
        set(this.fields, ['language', 'options'], value);
      },
      immediate: true,
    },
    interestsOptions: {
      handler(value) {
        set(this.fields, ['interests', 'options'], value);
      },
      immediate: true,
    },
    countries: {
      handler(value) {
        set(this.fields, ['country', 'options'], value);
      },
      immediate: true,
    },
    collections: {
      handler(value) {
        set(this.fields, ['collection', 'options'], value);
      },
      immediate: true,
    },
    stores: {
      handler(value) {
        set(this.fields, ['store', 'options'], value);
      },
      immediate: true,
    },
    salesforceDistrictOptions: {
      handler(value) {
        set(this.fields, ['salesforceDistrict', 'options'], value);
      },
      immediate: true,
    },
  },

  methods: {
    addTrackingInformation() {
      window.tracking.pushEvent(window.tracking.getGEPFormTracking());
    },
  },
};
</script>

<template>
  <div :data-cy="`${form.name}-form`">
    <multi-step-form
      :form="formForMultiStep"
      :redirect-url="redirectUrl"
      :terms-link="termsLink"
      :terms="terms"
      :steps="steps"
      :groups="groups"
      :countries="countries"
      :on-success-callback="addTrackingInformation"
      config-name="gep"
    />
  </div>
</template>
